import { create } from 'zustand';

import { BED_STATES, EMERGENCY_DEPT } from '../constants/occupancy';
import { getOccupancyBoard } from '../services/api/occupancyApi';
import { realtimeMiddleware } from './realtimeMiddleware';

/**
 * ═══════════════════════════════════════════════════════════════════════
 * Occupancy board store (C2 / FT2–FT3) — Zustand.
 *
 * WHY ITS OWN STORE (not bedInventoryStore): the board's ONE read endpoint is
 * `VIEW_OCCUPANCY_BOARD`-gated and serves the broadened nurse/reception audience
 * that LACKS `MANAGE_BED_CONFIG` — so it cannot ride the B3 inventory lists. It
 * hydrates from a SINGLE joined payload (inventory ⟕ BEDSTATE) and overlays live
 * `BED_STATE_CHANGED` WS events. The shared count-once selectors below
 * (selectOccupancyStats / selectDeptGroups) are the ONLY occupancy derivation in
 * the codebase — every surface consumes them (single-source-of-truth).
 *
 * Runtime safety (memory rule "no request storms"): `hydrate` is idempotent +
 * single-flight — ONE `getOccupancyBoard` call, a repeat while loaded is a no-op,
 * concurrent callers share one in-flight promise. Reconnect reuses the same
 * single-flight force path. A re-rendering effect can NEVER multiply requests.
 *
 * Realtime ordering guard (contract §3): `applyBedStateEvent` is idempotent by
 * `bed_id` and applies a frame ONLY when `evt.state_version > local.state_version`
 * — a stale / out-of-order / duplicate event NEVER regresses a chip. An event for
 * a bed not in inventory is ignored (the board enumerates from INVENTORY only).
 * ═══════════════════════════════════════════════════════════════════════
 */

// Fields a BED_STATE_CHANGED frame (or a POST 200 image) overlays onto a bed.
// `bed_category` (event) maps onto the board bed's `bed_category_code`.
// `death_recorded` (FE-5, S8) — SAME key, absent ⇒ false, on both the board
// payload and this WS frame; ALWAYS overwritten (never merged) so the flag
// going AWAY live (discharge, re-occupancy, or the record being removed —
// BE-6 clears it and re-emits each time) is applied exactly like it appearing.
function eventPatch(evt) {
  const patch = {
    state: evt.state,
    state_version: evt.state_version,
    updated_at: evt.updated_at ?? null,
    current_admission_id: evt.current_admission_id ?? null,
    patient_name: evt.patient_name ?? null,
    ip_number: evt.ip_number ?? null,
    blocked_reason: evt.blocked_reason ?? null,
    death_recorded: !!evt.death_recorded,
  };
  if (evt.bed_category != null) patch.bed_category_code = evt.bed_category;
  return patch;
}

/**
 * Pure monotonic-guard reducer (shared by the store action AND the realtime
 * middleware handler — single code path). Returns a NEW beds array iff the frame
 * is newer than the local bed; returns null for stale/dup/unknown-bed/no-op.
 */
export function reduceBedStateEvent(beds, evt) {
  if (!evt || !evt.bed_id || evt.state_version == null) return null;
  let applied = false;
  const next = beds.map((b) => {
    if (b.bed_id !== evt.bed_id) return b;
    if (evt.state_version <= (b.state_version ?? 0)) return b; // stale/dup — never regress
    applied = true;
    return { ...b, ...eventPatch(evt) };
  });
  return applied ? next : null;
}

const useOccupancyStore = create(realtimeMiddleware((set, get) => ({
  // ── State ──────────────────────────────────────────────────────────
  wards: [],
  rooms: [],
  beds: [],
  as_of: null,
  isLoading: false,
  error: null,
  loaded: false,      // "already loaded" guard — repeat hydrates no-op
  _inFlight: null,    // shared in-flight promise (single-flight)

  // ── Hydrate (bounded: single-flight + loaded guard; ONE board read) ──
  hydrate: async ({ force = false } = {}) => {
    const { _inFlight, loaded } = get();
    if (_inFlight) return _inFlight; // concurrent callers share the promise
    if (loaded && !force) {
      return { success: true, data: { wards: get().wards, rooms: get().rooms, beds: get().beds } };
    }
    set({ isLoading: true, error: null });
    const promise = (async () => {
      const res = await getOccupancyBoard();
      if (res.success) {
        set({
          wards: res.data.wards || [],
          rooms: res.data.rooms || [],
          beds: res.data.beds || [],
          as_of: res.data.as_of || null,
          isLoading: false,
          loaded: true,
          _inFlight: null,
        });
        return res;
      }
      set({ error: res.error || 'Failed to load the occupancy board.', isLoading: false, _inFlight: null });
      return res;
    })();
    set({ _inFlight: promise });
    return promise;
  },

  // Reconnect safety-net: reconcile the whole board with ONE forced read. Reuses
  // the single-flight path, so a reconnect + a mount effect can't double-fetch.
  refetchOnReconnect: () => get().hydrate({ force: true }),

  // ── Realtime: apply a BED_STATE_CHANGED frame (§3) ─────────────────
  // Idempotent by bed_id; monotonic version guard both directions. Unknown bed
  // → ignored (never enumerate from BEDSTATE). Returns true iff it applied.
  applyBedStateEvent: (evt) => {
    const next = reduceBedStateEvent(get().beds, evt);
    if (next) set({ beds: next });
    return next != null;
  },

  // ── Optimistic manual transition (FT8 popover) ─────────────────────
  // Apply the target locally NOW; the authoritative WS event (higher version)
  // supersedes it, or the caller reverts on API error. Snapshots the prior bed.
  // No version bump here — the real version arrives with the server frame, so a
  // late/stale event can still be guarded correctly.
  applyOptimistic: (bedId, targetState, { reason } = {}) => {
    let snapshot = null;
    set((s) => ({
      beds: s.beds.map((b) => {
        if (b.bed_id !== bedId) return b;
        snapshot = b;
        return {
          ...b,
          state: targetState,
          blocked_reason: targetState === 'BLOCKED' ? (reason || null) : null,
          ...(targetState === 'VACANT' || targetState === 'CLEANING'
            ? { patient_name: null, ip_number: null, current_admission_id: null }
            : {}),
        };
      }),
    }));
    return () => {
      if (!snapshot) return;
      set((s) => ({ beds: s.beds.map((b) => (b.bed_id === bedId ? snapshot : b)) }));
    };
  },

  // Merge a POST /state 200 image (same version-guard as a WS frame) so the
  // acting user's chip is authoritative even if the WS echo is delayed/dropped.
  applyServerBedState: (image) => get().applyBedStateEvent(image),

  clearError: () => set({ error: null }),

  reset: () => set({
    wards: [], rooms: [], beds: [], as_of: null,
    isLoading: false, error: null, loaded: false, _inFlight: null,
  }),
}), {
  storeName: 'occupancy',
  // Live board updates: a remote BED_STATE_CHANGED frame is applied through the
  // SAME monotonic-guard reducer (stale/dup never regress). Local events are
  // skipped by the middleware — our own optimistic paint goes via applyOptimistic,
  // never the bus — so there is no double-apply. No polling, no per-bed fetch:
  // the WS frame carries the full denormalized bed image (bounded requests).
  eventHandlers: {
    BED_STATE_CHANGED: (state, event) => {
      const next = reduceBedStateEvent(state.beds, event.payload);
      return next ? { beds: next } : {};
    },
  },
}));

export default useOccupancyStore;

/**
 * ═══════════════════════════════════════════════════════════════════════
 * SHARED COUNT-ONCE SELECTORS (FT3) — the SINGLE occupancy derivation in the
 * codebase. Org totals strip, per-ward mini-bar, per-dept totals, the dashboard
 * tile and any future consumer read THESE — never re-derive inline (single-
 * source-of-truth; the tooth-chart Done·2-vs-0 lesson).
 * ═══════════════════════════════════════════════════════════════════════
 */

const emptyByState = () => BED_STATES.reduce((a, code) => { a[code] = 0; return a; }, {});

// Board enumerates from INVENTORY; the server already excludes inactive rows,
// but we mirror selectInventoryStats' active guard so a stray inactive bed can
// never inflate a count (defensive parity with the B3 selector).
const isActiveBed = (b) => b.effective_active !== false;

/**
 * selectOccupancyStats(state) → count-once rollups, or `null` while the board is
 * not yet loaded (consumers render a loading state — NEVER a fake 0).
 *
 *   { org:     { total, byState },              // DISTINCT beds (dedup by bed_id)
 *     perWard: { [ward_id]: { total, byState } },
 *     perDept: { [dept_id]: { total, byState } } }  // NON-additive
 *
 * RC-6 dedupe + non-additivity: `org` counts each physical bed exactly ONCE
 * (deduped by bed_id) and is NEVER Σ(perDept). `perDept` counts a shared ward's
 * beds under EACH department the ward is tagged to — so a bed in a 2-dept ward
 * shows under both dept views but adds 1, not 2, to the org total.
 */
export function selectOccupancyStats(state) {
  const { loaded, wards = [], beds = [] } = state || {};
  if (!loaded) return null; // no fake zeros before the first load

  const org = { total: 0, byState: emptyByState() };
  const perWard = {};
  for (const w of wards) perWard[w.ward_id] = { total: 0, byState: emptyByState() };

  const seenOrg = new Set();
  for (const b of beds) {
    if (!isActiveBed(b) || seenOrg.has(b.bed_id)) continue;
    seenOrg.add(b.bed_id);
    const st = b.state || 'VACANT';
    org.total += 1;
    if (st in org.byState) org.byState[st] += 1;
    const pw = perWard[b.ward_id];
    if (pw) { pw.total += 1; if (st in pw.byState) pw.byState[st] += 1; }
  }

  // Per-dept view — deliberately NON-additive: a shared ward's beds are counted
  // under each of its departments (deduped by bed_id WITHIN a department).
  const deptsOfWard = {};
  for (const w of wards) deptsOfWard[w.ward_id] = w.department_ids || [];
  const perDept = {};
  const seenPerDept = {};
  for (const b of beds) {
    if (!isActiveBed(b)) continue;
    const st = b.state || 'VACANT';
    for (const d of deptsOfWard[b.ward_id] || []) {
      if (!perDept[d]) { perDept[d] = { total: 0, byState: emptyByState() }; seenPerDept[d] = new Set(); }
      if (seenPerDept[d].has(b.bed_id)) continue;
      seenPerDept[d].add(b.bed_id);
      perDept[d].total += 1;
      if (st in perDept[d].byState) perDept[d].byState[st] += 1;
    }
  }

  return { org, perWard, perDept };
}

/**
 * selectOccupancyTree(state) → ordered ward → room → bed grouping for the grid,
 * built off the INVENTORY payload. A ward with no rooms still appears (empty
 * rooms); a room with a sparse/VACANT bed set is never dropped (the server sends
 * VACANT version-0 rows for beds with no BEDSTATE — sparse-bed-not-hidden).
 *
 *   [{ ward, rooms: [{ room, beds: [bed…] }] }]
 */
export function selectOccupancyTree(state) {
  const { wards = [], rooms = [], beds = [] } = state || {};
  const bedsByRoom = {};
  for (const b of beds) (bedsByRoom[b.room_id] = bedsByRoom[b.room_id] || []).push(b);
  const roomsByWard = {};
  for (const r of rooms) (roomsByWard[r.ward_id] = roomsByWard[r.ward_id] || []).push(r);
  return wards.map((ward) => ({
    ward,
    rooms: (roomsByWard[ward.ward_id] || []).map((room) => ({
      room,
      beds: bedsByRoom[room.room_id] || [],
    })),
  }));
}

/**
 * selectDeptOptions(state) → sorted dept ids that TAG ≥1 ward (the single-source
 * option set for the dept filter — a dept with no ward isn't a useful board
 * filter, and this keeps the filter set and the grouping in lockstep).
 */
export function selectDeptOptions(state) {
  const { wards = [] } = state || {};
  const set = new Set();
  for (const w of wards) for (const d of (w.department_ids || [])) set.add(d);
  return Array.from(set).sort();
}

/**
 * selectDeptGroups(state, deptId) → rule-(a) VISIBILITY-UNION regroup for the
 * board, or `null` for a full-board no-op.
 *
 *   null                        ← "All departments" / EMERGENCY / unknown dept
 *   { pinned:[ward…], rest:[ward…] }  ← tagged wards pinned; EVERYTHING ELSE
 *                                        (org-wide, other-dept, dead-dept-only)
 *                                        stays in `rest` — NEVER excluded.
 *
 * Fails OPEN (null → flat full board) for an absent filter, the cross-dept
 * EMERGENCY audience, or a dept id that tags no ward — so a filter can never
 * hide a ward. This is deliberately NOT the B6 admin membership filter.
 */
export function selectDeptGroups(state, deptId) {
  const { loaded, wards = [] } = state || {};
  if (!loaded) return null;
  if (!deptId || deptId === EMERGENCY_DEPT) return null; // strict no-op — whole board
  const tagged = new Set();
  for (const w of wards) for (const d of (w.department_ids || [])) tagged.add(d);
  if (!tagged.has(deptId)) return null; // unknown dept → fail open, full board

  const pinned = [];
  const rest = [];
  for (const w of wards) {
    ((w.department_ids || []).includes(deptId) ? pinned : rest).push(w);
  }
  return { pinned, rest };
}
